import { createFileRoute } from "@tanstack/react-router";
import type { LiteClient } from "algoliasearch/lite";
import {
  AlertTriangleIcon,
  BadgeCheckIcon,
  ClockIcon,
  ListFilterIcon,
  ListIcon,
  ScanSearchIcon,
  SignatureIcon,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Configure, InstantSearch, useHits } from "react-instantsearch";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchClient } from "@/features/search/api/use-scoped-search-key";
import { ClearRefinements } from "@/features/search/components/clear-refinements";
import { RefinementCombobox } from "@/features/search/components/refinement-combobox";
import { SearchBox } from "@/features/search/components/search-box";
import { SearchPagination } from "@/features/search/components/search-pagination";
import { createPreviewAwareRouting } from "@/features/search/lib/preview-routing";
import { SignatureHitCard } from "@/features/signatures/components/signature-hit-card";
import {
  hasMultipleTenants,
  type SignatureHit,
  shortDate,
  signatureStatusConfig,
} from "@/features/signatures/lib/signature-hit";
import { CLAIM_PREVIEW_TABS, ClaimPreviewPanel } from "@/features/tasks/components/claim-preview-panel";
import { config } from "@/lib/config";
import { cn } from "@/lib/utils";

// Display-only page size — the effective hitsPerPage comes from the index config (Vue parity).
const HITS_PER_PAGE = 50;

// Loose so the InstantSearch routing params (?signature[...]) survive our own navigations.
const signaturesSearchSchema = z.looseObject({
  claim: z.string().optional().catch(undefined),
  claimTab: z.enum(CLAIM_PREVIEW_TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_authed/_dashboard/signatures/")({
  validateSearch: signaturesSearchSchema,
  component: SignaturesPage,
});

interface SignatureTab {
  name: string;
  filter: string;
  icon: LucideIcon;
  iconClass: string;
}

// The Vue page declared sort replicas but selectTab never switched the index — dead code, not ported.
const TABS: SignatureTab[] = [
  { name: "Signées", filter: "status:COMPLETED", icon: BadgeCheckIcon, iconClass: "text-emerald-500" },
  { name: "En attente", filter: "status:PENDING", icon: ClockIcon, iconClass: "text-amber-500" },
  { name: "Toutes", filter: "", icon: ListIcon, iconClass: "text-slate-400" },
  { name: "Expirées", filter: "status:EXPIRED", icon: AlertTriangleIcon, iconClass: "text-rose-500" },
];

const PREVIEW_PARAMS = ["claim", "claimTab"] as const;

/** Port of Signatures.vue — Algolia-driven feed of YouSign signature requests. */
function SignaturesPage() {
  const { client } = useSearchClient();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const openClaimPreview = (claimId: string) => {
    if (search.claim === claimId) {
      closeClaimPreview();
      return;
    }
    navigate({
      search: (prev) => ({ ...prev, claim: claimId, claimTab: "overview" as const }),
      resetScroll: false,
      replace: false,
    });
  };

  const closeClaimPreview = () => {
    navigate({
      search: (prev) => ({ ...prev, claim: undefined, claimTab: undefined }),
      replace: true,
      resetScroll: false,
    });
  };

  return (
    <div className="relative z-0 flex h-full flex-1 overflow-hidden bg-gradient-to-b from-white to-slate-50 text-left">
      <main className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-8 lg:p-12">
          <div className="mb-8 sm:flex-auto">
            <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">
              <SignatureIcon className="h-5 w-5 flex-none text-stone-400" aria-hidden="true" />
              Signatures
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Suivi des demandes de signature envoyées aux passagers
            </p>
          </div>

          {client ? (
            <SignaturesSearch
              client={client}
              selectedClaimId={search.claim ?? null}
              onClaimClick={openClaimPreview}
            />
          ) : (
            <SignaturesSearchSkeleton />
          )}
        </div>
      </main>

      {/* Right sidebar — claim preview driven by the ?claim search param */}
      {search.claim ? (
        <aside className="hidden bg-white lg:flex lg:w-full lg:max-w-[34.5rem] lg:shrink-0 lg:flex-col lg:border-l lg:border-gray-200">
          <ClaimPreviewPanel
            claimId={search.claim}
            activeTab={search.claimTab ?? "overview"}
            onTabChange={(tab) =>
              navigate({ search: (prev) => ({ ...prev, claimTab: tab }), replace: true, resetScroll: false })
            }
            onClose={closeClaimPreview}
          />
        </aside>
      ) : null}
    </div>
  );
}

/** Skeleton shown while the scoped Algolia key loads (search UI is gated on it). */
function SignaturesSearchSkeleton() {
  return (
    <div className="flex flex-col gap-y-6">
      <Skeleton className="h-10 w-96" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

interface SignaturesSearchProps {
  client: LiteClient;
  selectedClaimId: string | null;
  onClaimClick: (claimId: string) => void;
}

function SignaturesSearch({ client, selectedClaimId, onClaimClick }: SignaturesSearchProps) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const routing = useMemo(() => createPreviewAwareRouting(PREVIEW_PARAMS), []);

  const activeFilter = TABS[activeTabIndex]?.filter ?? "";

  return (
    <InstantSearch
      searchClient={client}
      indexName={config.ALGOLIA_SIGNATURE_INDEX}
      routing={routing}
      future={{ preserveSharedStateOnUnmount: true }}
    >
      <Configure filters={activeFilter} />

      {/* Tabs */}
      <div className="mb-4">
        <div className="sm:hidden">
          <label htmlFor="signature-tabs" className="sr-only">
            Sélectionner un onglet
          </label>
          <select
            id="signature-tabs"
            className="block w-full rounded-lg border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-rose-500"
            value={activeTabIndex}
            onChange={(event) => setActiveTabIndex(Number(event.currentTarget.value))}
          >
            {TABS.map((tab, index) => (
              <option key={tab.name} value={index}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav
            className="inline-flex items-center gap-x-0.5 rounded-lg bg-gray-100/80 p-0.5"
            aria-label="Tabs"
          >
            {TABS.map((tab, index) => {
              const Icon = tab.icon;
              const current = index === activeTabIndex;
              return (
                <button
                  key={tab.name}
                  type="button"
                  aria-current={current ? "page" : undefined}
                  onClick={() => setActiveTabIndex(index)}
                  className={cn(
                    "inline-flex items-center gap-x-1.5 rounded-md px-3 py-1.5 text-base font-medium transition-[color,background-color,box-shadow] duration-200",
                    current ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
                  )}
                >
                  <Icon className={cn("h-4 w-4", tab.iconClass)} />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Search + Filters bar */}
      <div className="inline-flex w-full items-center gap-x-4 rounded-xl border border-gray-200/70 bg-white p-4 shadow-sm">
        <div className="w-full">
          <SearchBox placeholder="Rechercher une signature..." />
        </div>
        <div className="mt-1">
          <Button variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters(!showFilters)}>
            <ListFilterIcon data-icon="inline-start" />
            Filtres
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "flex gap-x-8 rounded-b-xl border-x border-b border-gray-200/70 bg-white px-4 text-left",
          !showFilters && "hidden",
        )}
      >
        <RefinementCombobox
          attribute="travelAgencyOffice.travelAgency.name"
          attributeName="Agence de voyage"
        />
        <RefinementCombobox attribute="travelAgencyOffice.amadeusOfficeId" attributeName="Office ID" />
        <RefinementCombobox attribute="agent.email" attributeName="Agent" size="lg" />
        <RefinementCombobox
          attribute="travelAgencyCompany.displayName"
          attributeName="Entreprise"
          size="lg"
        />
      </div>

      <div>
        <ClearRefinements className="py-2" />
      </div>

      {/* Feed (timeline) */}
      <div className="mt-8">
        <SignaturesFeed selectedClaimId={selectedClaimId} onClaimClick={onClaimClick} />
      </div>

      <div className="py-10">
        <div className="overflow-hidden rounded-lg ring-1 ring-gray-200">
          <SearchPagination pageSize={HITS_PER_PAGE} />
        </div>
      </div>
    </InstantSearch>
  );
}

function SignaturesFeed({ selectedClaimId, onClaimClick }: Omit<SignaturesSearchProps, "client">) {
  const { items } = useHits<SignatureHit>();
  const showTenant = hasMultipleTenants(items);

  if (!items.length) {
    return (
      <div className="flex h-96 items-center rounded-2xl border border-gray-200/70 bg-white text-center">
        <div className="mx-auto flex w-full max-w-sm flex-col px-4">
          <div className="mx-auto rounded-full bg-slate-100 p-3 text-slate-500">
            <ScanSearchIcon className="h-6 w-6" />
          </div>
          <h1 className="mt-3 text-lg text-gray-800">Aucune signature</h1>
          <p className="mt-2 text-sm text-gray-500">
            Votre recherche ne correspond à aucune demande de signature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pl-4 pr-12">
      <div className="relative pl-20">
        {/* Vertical timeline line */}
        <div className="absolute bottom-0 left-[3.75rem] top-0 w-px bg-gray-200" aria-hidden="true" />

        {items.map((hit) => (
          <div key={hit.objectID} className="relative pb-8 last:pb-0">
            {/* Timeline dot (colored by status) */}
            <div
              className={cn(
                "absolute left-[-1.625rem] top-6 z-10 h-2 w-2 rounded-full ring-[3px] ring-white",
                signatureStatusConfig[hit.status]?.dotBg ?? "bg-slate-300",
              )}
            />

            {/* Date label on the timeline */}
            <div className="absolute left-[-5rem] top-5 w-[3rem] text-right text-[10px] font-medium uppercase leading-tight tracking-wide text-slate-400">
              {shortDate(hit)}
            </div>

            <SignatureHitCard
              hit={hit}
              selected={selectedClaimId === hit.claimId}
              showTenant={showTenant}
              onClick={() => onClaimClick(hit.claimId)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
